import React, { useState, useContext, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Card, Badge, Button, Table, Alert, Spinner, Form, InputGroup, Dropdown, ButtonGroup } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faTrash, faRedo, faDownload, faEllipsisV, faUnlink, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import { useQueryClient } from 'react-query'
import {
  useReceiptScans,
  useDeleteReceiptScan,
  useUnmatchReceiptScan,
  useRetryReceiptScan,
  useBulkDeleteReceiptScans,
  useBulkRetryReceiptScans
} from 'api/hooks/use-receipt-scans'
import { baseUrl } from 'api/helpers'
import { formatDateLocal, formatDate } from 'helpers/datetime'
import UserContext from 'context/user-context'
import TableFooter from 'shared/tables/table-footer'
import ReceiptDropzone from './ReceiptDropzone'
import ReceiptMatchingModal from './ReceiptMatchingModal'
import QuickLookDrawer from 'transactions/drawer'

// Maps UI filter values to the status values sent to the server.
// 'uploading' is a virtual tab combining pending + processing.
const statusParamForFilter = (filter) => {
  if (filter === 'all') return undefined
  if (filter === 'uploading') return 'pending,processing'
  return filter
}

const ReceiptManagement = ({ municipalityId: propMunicipalityId, scanType = 'receipt' }) => {
  const isInvoice = scanType === 'invoice'
  const documentLabel = isInvoice ? 'Invoice' : 'Receipt'
  const documentLabelLower = isInvoice ? 'invoice' : 'receipt'
  const user = useContext(UserContext)
  const municipalityId = propMunicipalityId || user?.municipality_id
  const [selectedReceipt, setSelectedReceipt] = useState(null)
  const [showMatchingModal, setShowMatchingModal] = useState(false)
  const [showTransactionDrawer, setShowTransactionDrawer] = useState(false)
  const [selectedTransactionId, setSelectedTransactionId] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedIds, setSelectedIds] = useState([])
  const [filterParams, setFilterParams] = useState({ page: 1, perPage: 25 })
  // Tracks whether we've already auto-switched the tab for the current upload
  // batch. Prevents per-file completions from stomping on the user's manual
  // tab selection while uploads are still in flight.
  const autoSwitchedThisBatchRef = useRef(false)
  const prevHasProcessingRef = useRef(false)
  const queryClient = useQueryClient()

  // Reset to page 1 whenever the status filter or search changes.
  useEffect(() => {
    setFilterParams((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }))
  }, [statusFilter, searchTerm])

  const { data: receiptsData, isLoading, refetch } = useReceiptScans({
    params: {
      municipality_id: municipalityId,
      scan_type: scanType,
      search: searchTerm || undefined,
      status: statusParamForFilter(statusFilter),
      page: filterParams.page,
      perPage: filterParams.perPage
    },
    options: {
      // Keep the last page of data (and its status_counts) visible while the
      // next query resolves so tab badges don't flicker to 0 on tab click.
      keepPreviousData: true,
      refetchInterval: (data) => {
        // Auto-refresh every 3 seconds while anything is still in flight.
        const counts = data?.meta?.status_counts || {}
        const hasProcessing = (counts.pending || 0) + (counts.processing || 0) > 0
        return hasProcessing ? 3000 : false
      }
    }
  })

  // Mutations
  const deleteMutation = useDeleteReceiptScan({
    options: {
      onSuccess: () => {
        toast.success('Receipt deleted successfully')
        refetch()
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to delete receipt'
        toast.error(errorMessage)
      }
    }
  })

  const unmatchMutation = useUnmatchReceiptScan({
    options: {
      onSuccess: () => {
        toast.success('Receipt unmatched successfully')
        queryClient.invalidateQueries(['receipt-scans'])
        queryClient.invalidateQueries(['transactions'])
        refetch()
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to unmatch receipt'
        toast.error(errorMessage)
      }
    }
  })

  const retryMutation = useRetryReceiptScan({
    options: {
      onSuccess: () => {
        toast.success('Receipt queued for retry')
        refetch()
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to retry receipt'
        toast.error(errorMessage)
      }
    }
  })

  const bulkDeleteMutation = useBulkDeleteReceiptScans({
    options: {
      onSuccess: () => {
        toast.success('Receipts deleted successfully')
        setSelectedIds([])
        refetch()
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to delete receipts'
        toast.error(errorMessage)
      }
    }
  })

  const bulkRetryMutation = useBulkRetryReceiptScans({
    options: {
      onSuccess: () => {
        toast.success('Receipts queued for retry')
        setSelectedIds([])
        refetch()
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to retry receipts'
        toast.error(errorMessage)
      }
    }
  })

  const receipts = receiptsData?.data || []
  const meta = receiptsData?.meta || {}
  const serverStatusCounts = meta.status_counts || {}

  // Keep selectedReceipt in sync with latest query data
  useEffect(() => {
    if (selectedReceipt) {
      const updated = receipts.find((r) => r.id === selectedReceipt.id)
      if (updated) {
        setSelectedReceipt(updated)
      }
    }
  }, [receipts])

  const hasProcessingReceipts =
    (serverStatusCounts.pending || 0) + (serverStatusCounts.processing || 0) > 0
  const totalReceiptsAcrossStatuses = Object.values(serverStatusCounts).reduce(
    (sum, n) => sum + (n || 0),
    0
  )

  // Once a batch transitions from processing → done, allow the next upload to
  // auto-switch again. Only reset on the transition so a manual tab click
  // before the server's pending state is visible to polling isn't clobbered.
  useEffect(() => {
    if (prevHasProcessingRef.current && !hasProcessingReceipts) {
      autoSwitchedThisBatchRef.current = false
    }
    prevHasProcessingRef.current = hasProcessingReceipts
  }, [hasProcessingReceipts])

  // Switch to Needs Review once all uploads finish processing
  useEffect(() => {
    if (statusFilter === 'uploading' && !hasProcessingReceipts && totalReceiptsAcrossStatuses > 0) {
      setStatusFilter('manual_review')
    }
  }, [hasProcessingReceipts, statusFilter, totalReceiptsAcrossStatuses])

  const handleUploadSuccess = () => {
    // Auto-switch to the Uploading view only once per batch so subsequent file
    // completions don't override a tab the user has clicked into.
    if (!autoSwitchedThisBatchRef.current) {
      autoSwitchedThisBatchRef.current = true
      setStatusFilter('uploading')
    }
    refetch()
  }

  const handleStatusFilterClick = (filter) => {
    // Any manual tab click locks auto-switching for the rest of this batch.
    autoSwitchedThisBatchRef.current = true
    setStatusFilter(filter)
  }

  const handleReviewClick = (receipt) => {
    setSelectedReceipt(receipt)
    setShowMatchingModal(true)
  }

  const handleDeleteClick = (receipt) => {
    if (window.confirm('Are you sure you want to delete this receipt?')) {
      deleteMutation.mutate(receipt.id)
    }
  }

  const handleUnmatchClick = (receipt) => {
    if (window.confirm('Are you sure you want to unmatch this receipt? It will be set to manual review status.')) {
      unmatchMutation.mutate(receipt.id)
    }
  }

  const handleRetryClick = (receipt) => {
    retryMutation.mutate(receipt.id)
  }

  const handleDownloadClick = (receipt) => {
    if (receipt.receipt_image_url) {
      window.open(`${baseUrl}${receipt.receipt_image_url}`, '_blank')
    }
  }

  const handleViewTransactionClick = (receipt) => {
    if (receipt.transaction_id) {
      setSelectedTransactionId(receipt.transaction_id)
      setShowTransactionDrawer(true)
    }
  }

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      toast.warning('Please select receipts to delete')
      return
    }
    if (window.confirm(`Are you sure you want to delete ${selectedIds.length} receipt(s)?`)) {
      bulkDeleteMutation.mutate(selectedIds)
    }
  }

  const handleBulkRetry = () => {
    if (selectedIds.length === 0) {
      toast.warning('Please select receipts to retry')
      return
    }
    bulkRetryMutation.mutate(selectedIds)
  }

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(receipts.map((r) => r.id))
    } else {
      setSelectedIds([])
    }
  }

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const isAllSelected = receipts.length > 0 && selectedIds.length === receipts.length
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < receipts.length

  // Counts come from the server (computed before the status filter) so each tab
  // shows the full count regardless of which tab is currently active.
  const statusCounts = {
    all: totalReceiptsAcrossStatuses,
    pending: serverStatusCounts.pending || 0,
    processing: serverStatusCounts.processing || 0,
    matched: serverStatusCounts.matched || 0,
    manual_review: serverStatusCounts.manual_review || 0,
    failed: serverStatusCounts.failed || 0
  }

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const getStatusBadge = (status) => {
    if (status === 'processing') {
      return (
        <Badge bg="" className="badge-info">
          <Spinner animation="border" size="sm" className="me-1" style={{ width: '0.6rem', height: '0.6rem' }} />
          Processing
        </Badge>
      )
    }

    const statusMap = {
      pending: { className: 'badge-secondary', text: 'Pending' },
      matched: { className: 'badge-primary', text: 'Matched' },
      manual_review: { className: 'badge-warning', text: 'Needs Review' },
      failed: { className: 'badge-danger', text: 'Failed' }
    }

    const config = statusMap[status] || { className: 'badge-secondary', text: status }
    return <Badge bg="" className={config.className}>{config.text}</Badge>
  }

  const getMatchTypeBadge = (matchType) => {
    if (!matchType) return null
    const text = matchType === 'manual' ? 'Manually Matched' : 'Matched'
    return <Badge bg="" className="badge-primary">{text}</Badge>
  }

  return (
    <div className="receipt-management">
      {/* Upload Section */}
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">Upload {documentLabel}s</h5>
          <p className="text-muted mb-3">
            {isInvoice
              ? 'Upload invoice images or PDFs. The system will automatically extract information using OCR and attempt to match them to existing AP transactions.'
              : 'Upload receipt images or PDFs. The system will automatically extract information using OCR and attempt to match them to existing PCard transactions.'
            }
          </p>
          <ReceiptDropzone onUploadSuccess={handleUploadSuccess} scanType={scanType} />
        </Card.Body>
      </Card>

      {/* Receipts Table Section */}
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center">
              <h5 className="mb-0">{isInvoice ? 'Invoices' : 'Receipts'}</h5>
              {hasProcessingReceipts && (
                <span className="ms-2 text-muted d-flex align-items-center">
                  <Spinner size="sm" animation="border" className="me-1" />
                  <small>Processing {statusCounts.pending + statusCounts.processing} {documentLabelLower}{statusCounts.pending + statusCounts.processing !== 1 ? 's' : ''}...</small>
                </span>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button
                variant={statusFilter === 'all' ? 'primary' : 'light'}
                size="sm"
                onClick={() => handleStatusFilterClick('all')}
              >
                All ({statusCounts.all})
              </Button>
              <Button
                variant={statusFilter === 'manual_review' ? 'warning' : 'light'}
                size="sm"
                onClick={() => handleStatusFilterClick('manual_review')}
              >
                Needs Review ({statusCounts.manual_review})
              </Button>
              <Button
                variant={statusFilter === 'matched' ? 'success' : 'light'}
                size="sm"
                onClick={() => handleStatusFilterClick('matched')}
              >
                Matched ({statusCounts.matched})
              </Button>
              <Button
                variant={statusFilter === 'uploading' ? 'info' : 'light'}
                size="sm"
                onClick={() => handleStatusFilterClick('uploading')}
              >
                Processing ({statusCounts.pending + statusCounts.processing})
              </Button>
              <Button
                variant={statusFilter === 'failed' ? 'danger' : 'light'}
                size="sm"
                onClick={() => handleStatusFilterClick('failed')}
              >
                Failed ({statusCounts.failed})
              </Button>
            </div>
          </div>

          {/* Search and Bulk Actions */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <InputGroup style={{ maxWidth: '400px' }}>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by vendor name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button variant="light" onClick={() => setSearchTerm('')}>
                  Clear
                </Button>
              )}
            </InputGroup>

            {selectedIds.length > 0 && (
              <div className="d-flex gap-2 align-items-center">
                <span className="text-muted">
                  {selectedIds.length} selected
                </span>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteMutation.isLoading}
                >
                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                  Delete
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleBulkRetry}
                  disabled={bulkRetryMutation.isLoading}
                >
                  <FontAwesomeIcon icon={faRedo} className="me-1" />
                  Retry
                </Button>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : receipts.length === 0 ? (
            <Alert variant="info">
              No {documentLabelLower}s found. Upload {isInvoice ? 'an' : 'a'} {documentLabelLower} to get started.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <Form.Check
                        type="checkbox"
                        checked={isAllSelected}
                        ref={(input) => {
                          if (input) input.indeterminate = isSomeSelected
                        }}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Status</th>
                    <th>File</th>
                    <th>Uploaded</th>
                    <th>Vendor</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th style={{ width: '150px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((receipt) => {
                    return (
                      <tr
                        key={receipt.id}
                        onClick={() => handleReviewClick(receipt)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <Form.Check
                            type="checkbox"
                            checked={selectedIds.includes(receipt.id)}
                            onChange={() => handleSelectOne(receipt.id)}
                          />
                        </td>
                        <td>{receipt.match_type ? getMatchTypeBadge(receipt.match_type) : getStatusBadge(receipt.status)}</td>
                        <td className="text-truncate" style={{ maxWidth: '200px' }}><small>{receipt.file_name || 'N/A'}</small></td>
                        <td>{formatDate(receipt.created_at)}</td>
                        <td>{receipt.effective_vendor_name || receipt.ocr_vendor_name || 'N/A'}</td>
                        <td>{formatCurrency(receipt.effective_total_amount || receipt.ocr_total_amount)}</td>
                        <td>{formatDateLocal(receipt.effective_transaction_date || receipt.ocr_transaction_date)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {receipt.status === 'failed' && (
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleRetryClick(receipt)}
                              className="me-1"
                            >
                              <FontAwesomeIcon icon={faRedo} />
                            </Button>
                          )}
                          <Dropdown as={ButtonGroup} size="sm">
                            <Dropdown.Toggle variant="light" id={`dropdown-${receipt.id}`}>
                              <FontAwesomeIcon icon={faEllipsisV} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu
                              renderOnMount
                              align="end"
                              popperConfig={{ strategy: 'fixed' }}
                            >
                              <Dropdown.Item onClick={() => handleDownloadClick(receipt)}>
                                <FontAwesomeIcon icon={faDownload} className="me-2" />
                                Download
                              </Dropdown.Item>
                              {receipt.status === 'matched' && (
                                <>
                                  <Dropdown.Item onClick={() => handleViewTransactionClick(receipt)}>
                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                                    View Transaction
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleUnmatchClick(receipt)}>
                                    <FontAwesomeIcon icon={faUnlink} className="me-2" />
                                    Unmatch
                                  </Dropdown.Item>
                                </>
                              )}
                              {(receipt.status === 'failed' || receipt.status === 'matched') && (
                                <Dropdown.Item onClick={() => handleRetryClick(receipt)}>
                                  <FontAwesomeIcon icon={faRedo} className="me-2" />
                                  Retry OCR
                                </Dropdown.Item>
                              )}
                              <Dropdown.Divider />
                              <Dropdown.Item
                                onClick={() => handleDeleteClick(receipt)}
                                className="text-danger"
                              >
                                <FontAwesomeIcon icon={faTrash} className="me-2" />
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {meta.pages > 0 && receipts.length > 0 && (
            <div className="mt-3">
              <TableFooter
                meta={meta}
                params={filterParams}
                setParams={setFilterParams}
              />
            </div>
          )}
        </Card.Body>
      </Card>

      <ReceiptMatchingModal
        show={showMatchingModal}
        onHide={() => {
          setShowMatchingModal(false)
          setSelectedReceipt(null)
          refetch()
        }}
        receiptScan={selectedReceipt}
        receipts={receipts}
        onChangeReceipt={setSelectedReceipt}
        scanType={scanType}
      />

      {showTransactionDrawer && selectedTransactionId && (
        <QuickLookDrawer
          transactions={[selectedTransactionId]}
          transactionId={selectedTransactionId}
          show={showTransactionDrawer}
          setShow={setShowTransactionDrawer}
        />
      )}
    </div>
  )
}

ReceiptManagement.propTypes = {
  municipalityId: PropTypes.string,
  scanType: PropTypes.string
}

ReceiptManagement.defaultProps = {
  municipalityId: null,
  scanType: 'receipt'
}

export default ReceiptManagement
